/**
 * UserRole Value Object
 * Represents the different roles a user can have in the system
 *
 * DB Mapping: role ENUM (DIRECTOR, TEACHER, STUDENT, ADMIN) -> lowercase for app usage
 */
export type UserRole = 'director' | 'teacher' | 'student' | 'admin';

export const UserRoles = {
  DIRECTOR: 'director' as UserRole,
  TEACHER: 'teacher' as UserRole,
  STUDENT: 'student' as UserRole,
  ADMIN: 'admin' as UserRole,
};

/**
 * Legacy role aliases for backwards compatibility
 * @deprecated Use UserRoles instead
 */
export const LegacyUserRoles = {
  PROFESOR: 'teacher' as UserRole,
  ALUMNO: 'student' as UserRole,
};

export function isValidUserRole(role: string): role is UserRole {
  return ['director', 'teacher', 'student', 'admin'].includes(role);
}

/**
 * Convert database role (UPPERCASE) to application role (lowercase)
 */
export function fromDatabaseRole(dbRole: string): UserRole {
  const roleMap: Record<string, UserRole> = {
    'DIRECTOR': 'director',
    'TEACHER': 'teacher',
    'STUDENT': 'student',
    'ADMIN': 'admin',
  };
  const role = roleMap[dbRole];
  if (!role) {
    throw new Error(`Invalid database role: ${dbRole}`);
  }
  return role;
}

/**
 * Convert application role (lowercase) to database role (UPPERCASE)
 */
export function toDatabaseRole(role: UserRole): string {
  return role.toUpperCase();
}

/**
 * Get display name for a role in Spanish
 */
export function getRoleDisplayName(role: UserRole): string {
  const displayNames: Record<UserRole, string> = {
    'director': 'Director',
    'teacher': 'Profesor',
    'student': 'Alumno',
    'admin': 'Administrador',
  };
  return displayNames[role];
}

/**
 * Get all roles with their display names
 */
export function getAllRolesWithDisplayNames(): Array<{ role: UserRole; displayName: string }> {
  return [
    { role: UserRoles.DIRECTOR, displayName: getRoleDisplayName(UserRoles.DIRECTOR) },
    { role: UserRoles.TEACHER, displayName: getRoleDisplayName(UserRoles.TEACHER) },
    { role: UserRoles.STUDENT, displayName: getRoleDisplayName(UserRoles.STUDENT) },
    { role: UserRoles.ADMIN, displayName: getRoleDisplayName(UserRoles.ADMIN) },
  ];
}

/**
 * Get only public registration roles (student and teacher)
 * Used for public registration form - excludes director and admin roles
 */
export function getPublicRegistrationRoles(): Array<{ value: string; label: string }> {
  return [
    { value: 'student', label: 'Estudiante' },
    { value: 'teacher', label: 'Profesor' },
  ];
}
